import { memo, useEffect, useEffectEvent, useMemo, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconFileCertificate } from '@tabler/icons-react';
import { Widget } from '@typeform/embed-react';
import { IconCircleCheckDisabled, IconCircleDisabled } from 'icons';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Modal, ModalBody, Typography } from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import { LanguageObject } from '@/types/types';

import ConfirmationModal from 'components/popups/ConfirmationModal';
import Constants from 'constants/index';
import { getNameFromLanguage } from 'utils/language';

import { PlanAssignment, PlanStep } from '@/_generated/api-clients/xp';

interface PlanItemQuizProps {
  disabled?: boolean;
  isActive: boolean;
  isComplete: boolean;
  language: string;
  onClick: (step: PlanStep) => void;
  onRefetch: () => void;
  planId: string;
  statuses: PlanAssignment[];
  step: PlanStep;
  users: string[];
}

const PlanItemQuiz: React.FC<PlanItemQuizProps> = ({
  disabled,
  isActive,
  isComplete,
  language,
  onClick,
  onRefetch,
  planId,
  statuses,
  step,
  users,
}) => {
  const { t } = useTranslation();
  const [lastPolledEventId, setLastPolledEventId] = useState('');
  const [activeQuizScore, setActiveQuizScore] = useState<number | null>(null);
  const [showPassPopup, setShowPassPopup] = useState(false);
  const [showFailPopup, setShowFailPopup] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);
  const [pollingRetry, setPollingRetry] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuizResultsDelayed, setShowQuizResultsDelayed] = useState(false);
  const [showQuizResultsIssue, setShowQuizResultsIssue] = useState(false);
  const [quizKey, setQuizKey] = useState<string>();

  const maxPollingCount = 7; // we poll every 2 seconds, this number would correspond to the time spent polling. 14 seconds at 7

  // list of status objects for current user(s)
  const userStatuses = statuses.filter(status =>
    users.find(id => status.userId === id),
  );

  // finds the status of first user taking the quiz that has not passed the quiz
  let activeQuizUserStatuses = userStatuses.filter(userStatus =>
    userStatus.steps?.find(
      stepStatus =>
        stepStatus.stepId === step.id &&
        stepStatus.result !== Constants.QUIZ_RESULTS.PASS,
    ),
  );
  if (activeQuizUserStatuses.length === 0) {
    activeQuizUserStatuses = userStatuses;
  }

  const currentUserStatus = activeQuizUserStatuses.find(
    userStatus => userStatus.userId === users[0],
  );

  const quizStatus = currentUserStatus?.steps?.find(
    stepStatus => stepStatus.stepId === step.id,
  );

  // Use `duration` (API field) not `stepDuration` (legacy normalized field).
  // See PATHWAY-2935 for context.
  const stepDuration = Math.round((quizStatus?.duration ?? 0) / 60);

  const currentStatusStep = activeQuizUserStatuses
    .at(0)
    ?.steps?.find(stepStatus => stepStatus.stepId === step.id);

  const currentStepEventId = activeQuizUserStatuses
    .at(0)
    ?.steps?.find(stepStatus => stepStatus.stepId === step.id)?.eventId;

  const notifyQuizError = (message: string) => {
    datadogRum.addError(new Error(message), {
      section: 'quiz',
      stepId: step.id,
      quizId: step.quizId,
      stepName: step.name,
      planId,
      users,
      pollingCount,
      lastPolledEventId,
      currentStepEventId,
      currentUserStatus,
      quizStatus,
    });
  };

  const lastPolledEvent = useEffectEvent(() => {
    if (isComplete || !currentStepEventId) {
      return;
    }
    setLastPolledEventId(currentStepEventId);
  });

  useEffect(() => {
    lastPolledEvent();
  }, []);

  const handleQuizSubmit = () => {
    setIsLoading(true);
    setPollingCount(1);
  };

  useEffect(() => {
    if (pollingCount > maxPollingCount) {
      if (pollingRetry) {
        setShowQuizResultsIssue(true);
        notifyQuizError('Quiz results issue - polling exhausted after retry');
      } else {
        setShowQuizResultsDelayed(true);
        notifyQuizError('Quiz results delayed - polling timeout');
      }
    } else if (pollingCount) {
      const eventIdsAreSame = currentStepEventId === lastPolledEventId;
      if (eventIdsAreSame || !currentStepEventId) {
        setTimeout(() => {
          onRefetch();
          setPollingCount(prevState => prevState + 1);
        }, 2000);
      } else {
        setIsLoading(false);
        setPollingCount(0);
        setPollingRetry(false);
        setLastPolledEventId(currentStepEventId);
        if (currentStatusStep?.result === Constants.QUIZ_RESULTS.PASS) {
          setActiveQuizScore(
            Math.round(
              ((currentStatusStep.score ?? 0) /
                (currentStatusStep.totalQuestions ?? 1)) *
                100,
            ),
          );
          setShowPassPopup(true);
        } else {
          setShowFailPopup(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingCount]);

  const handlePassContinue = () => {
    onRefetch();
    setShowPassPopup(false);
  };

  const handleFailContinue = () => {
    onRefetch();
    setQuizKey(uuidv4());
    setShowFailPopup(false);
  };

  const handleRetakeQuiz = () => {
    setQuizKey(uuidv4());
    setPollingCount(0);
    setIsLoading(false);
    setPollingRetry(false);
    setShowQuizResultsIssue(false);
    setShowQuizResultsDelayed(false);
    onRefetch();
  };

  const handleClick = () => {
    setQuizKey(uuidv4());
    onClick(step);
  };

  const quizWidget = useMemo(
    () => (
      <Widget
        className="w-full!"
        disableScroll={true}
        hidden={{
          userIds: users.toString(),
          checklistId: planId,
          stepId: step.id,
          language: language,
        }}
        hideHeaders={true}
        id={step.quizId ?? ''}
        key={quizKey}
        onSubmit={() => {
          handleQuizSubmit();
        }}
      />
    ),
    [language, planId, quizKey, step, users],
  );

  return (
    <>
      <div>
        <div
          className={cn(
            'flex w-full flex-row items-center gap-2',
            !disabled && 'cursor-pointer',
            isActive && !isComplete && 'pb-2.5',
          )}
        >
          {isComplete ? (
            <div className="mr-2">
              <IconCircleCheckDisabled />
            </div>
          ) : (
            <div className="mr-2 cursor-default">
              <IconCircleDisabled />
            </div>
          )}
          <button
            type="button"
            className={cn(
              'flex w-full flex-col text-left',
              !disabled && 'cursor-pointer',
            )}
            disabled={disabled}
            onClick={handleClick}
          >
            <Typography
              data-testid="TaskTitle"
              variant="overline3"
              className="text-grayscale-gray6!"
            >
              {t('Generic.quiz')}
              {isComplete &&
                ` - ${t('Generic.completedIn')} ${stepDuration} ${t(
                  'Generic.mins',
                )}`}
            </Typography>
            <Typography data-testid="QuizName" variant="body1">
              {getNameFromLanguage(step.name as LanguageObject, language)}
            </Typography>
          </button>
          <button
            type="button"
            className={cn(
              'text-grayscale-gray2 mx-1.5 flex w-6',
              !disabled && 'cursor-pointer',
            )}
            disabled={disabled}
            onClick={handleClick}
          >
            <IconFileCertificate className="size-6!" />
          </button>
        </div>
        {isComplete && (
          <Typography
            data-testid="PassMessage"
            variant="body1"
            className="text-feedback-success-base! pl-10!"
          >
            {users.length === 1 && quizStatus?.score
              ? t('TrainingPlans.quizzes.quizSuccessPopup', {
                  score: `${Math.round(
                    (quizStatus.score / (quizStatus.totalQuestions ?? 1)) * 100,
                  )}`,
                })
              : t('TrainingPlans.quizzes.quizPassed')}
          </Typography>
        )}
      </div>
      {isActive && !isComplete && (
        <div className="flex h-[500px] w-full">{quizWidget}</div>
      )}
      <ConfirmationModal
        bodyText={t('TrainingPlans.quizzes.quizSuccessPopup', {
          score: activeQuizScore,
        })}
        bodyTextColor="success"
        headerText={t('TrainingPlans.quizzes.congratulations')}
        isOpen={showPassPopup}
        onClose={handlePassContinue}
        overlayProps={{ style: { zIndex: 10003 } }}
        primaryButtonHandler={handlePassContinue}
        primaryButtonText={t('Button.continue')}
        className="z-10003!"
      />
      <ConfirmationModal
        bodyText={t('TrainingPlans.quizzes.quizFail')}
        headerText={t('TrainingPlans.quizzes.youDidNotPass')}
        isOpen={showFailPopup}
        onClose={handleFailContinue}
        overlayProps={{ style: { zIndex: 10003 } }}
        primaryButtonHandler={handleFailContinue}
        primaryButtonText={t('Button.continue')}
        className="z-10003!"
      />
      <ConfirmationModal
        bodyText={t('TrainingPlans.quizzes.quizResultsDelayed')}
        headerText={t('TrainingPlans.quizzes.calculatingQuiz')}
        isOpen={showQuizResultsDelayed}
        overlayProps={{ style: { zIndex: 10003 } }}
        primaryButtonHandler={handleRetakeQuiz}
        primaryButtonText={t('Button.retake')}
        secondaryButtonHandler={() => {
          setPollingCount(1);
          setPollingRetry(true);
          setShowQuizResultsDelayed(false);
        }}
        secondaryButtonText={t('Button.wait')}
        showCloseButton={false}
        className="z-10003!"
      />
      <ConfirmationModal
        bodyText={t('TrainingPlans.quizzes.quizResultsIssue')}
        headerText={t('TrainingPlans.quizzes.calculatingQuiz')}
        isOpen={showQuizResultsIssue}
        overlayProps={{ style: { zIndex: 10003 } }}
        primaryButtonHandler={handleRetakeQuiz}
        primaryButtonText={t('Button.retake')}
        showCloseButton={false}
        className="z-10003!"
      />
      {/* @ts-expect-error overload error from CFA DS **/}
      <Modal
        disableOverlayClick={true}
        overlayProps={{ style: { zIndex: 10002 } }}
        show={isLoading}
        className="z-10002!"
      >
        <ModalBody className="px-[133px]! py-[92px]!">
          <Typography color="default" variant="h3">
            {t('TrainingPlans.quizzes.quizWaitingForResults')}
          </Typography>
        </ModalBody>
      </Modal>
    </>
  );
};

// memo to prevent re-renders when taking the quiz
export default memo(PlanItemQuiz);
